import type { AuditDependency } from '../schemas/index';
import {
  dep,
  ManifestParseError,
  type ManifestParser,
  type ParsedManifest,
  rangeDep,
  type SkippedDependency,
} from './types';

/**
 * Exact-version regex for npm/package.json.
 *
 * A requirement is EXACT if and only if it is a bare semver X.Y.Z optionally
 * with a prerelease/build suffix. Caret, tilde, comparator and x-ranges are
 * ranges, resolved to their upper bound by the Worker. Everything else
 * (`latest`, `workspace:`, `file:`, URLs) has no registry version at all.
 */
const EXACT_VERSION_RE = /^\d+\.\d+\.\d+([-+].*)?$/;

type Classification = { kind: 'exact' } | { kind: 'range' } | { kind: 'skip'; reason: string };

/** Sorts a requirement string into exact pin, resolvable range, or neither. */
function classifyRequirement(req: string): Classification {
  const t = req.trim();

  if (t === '') {
    return { kind: 'skip', reason: 'no version specified' };
  }

  // workspace:, file:, link:, git+…, http(s):, npm: aliases, etc.
  if (/^(workspace|file|link|git\+|git:|github:|bitbucket:|gitlab:|http:|https:|npm:)/i.test(t)) {
    return { kind: 'skip', reason: 'not a registry version' };
  }

  // Tags like `latest`, `next`, `beta`, `canary`
  if (/^[a-zA-Z][a-zA-Z0-9._-]*$/.test(t)) {
    return { kind: 'skip', reason: 'not a registry version' };
  }

  if (EXACT_VERSION_RE.test(t)) {
    return { kind: 'exact' };
  }

  // ^, ~, >=, <=, >, <, ||, x-ranges, comma ranges, partial versions (1.2), *
  return { kind: 'range' };
}

/**
 * Parses an `npm` / `package.json` manifest.
 *
 * Collects pins and ranges from `dependencies`, `devDependencies`, and
 * `optionalDependencies`. The first occurrence of a name wins (deduplication).
 */
export const parsePackageJson: ManifestParser = (content: string): ParsedManifest => {
  // Normalise line endings before passing to JSON.parse (JSON.parse is fine
  // with \r\n but this keeps behaviour consistent with other parsers).
  const normalised = content.replace(/\r\n/g, '\n');

  let parsed: unknown;
  try {
    parsed = JSON.parse(normalised);
  } catch {
    throw new ManifestParseError('Invalid JSON: could not parse package.json');
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new ManifestParseError('package.json must be a JSON object');
  }

  const pkg = parsed as Record<string, unknown>;

  const dependencies: AuditDependency[] = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>();

  const sections = ['dependencies', 'devDependencies', 'optionalDependencies'] as const;

  for (const section of sections) {
    const raw = pkg[section];
    if (raw == null) continue;
    if (typeof raw !== 'object' || Array.isArray(raw)) continue;

    const depsMap = raw as Record<string, unknown>;
    for (const [name, req] of Object.entries(depsMap)) {
      if (seen.has(name)) continue; // first-occurrence wins
      seen.add(name);

      if (typeof req !== 'string') {
        skipped.push({ name, requirement: null, reason: 'no version specified' });
        continue;
      }

      const classified = classifyRequirement(req);
      if (classified.kind === 'exact') {
        dependencies.push(dep('NPM', name, req.trim()));
      } else if (classified.kind === 'range') {
        dependencies.push(rangeDep('NPM', name, req));
      } else {
        skipped.push({ name, requirement: req, reason: classified.reason });
      }
    }
  }

  return { system: 'NPM', dependencies, skipped };
};
